import React from 'react';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import negativeIcon from '../../../../assets/icon/negative.svg';
import neutralIcon from '../../../../assets/icon/neutral.svg';
import positiveIcon from '../../../../assets/icon/positive.svg';
import { formatPercent, renderIfMoreThan10Percent } from '../../../../libs/format';
import { TitleWithHelp } from '../../AnalyticsShared/AnalyticsContentWrapper';
import { OpacityContentWrapper } from '../../AnalyticsShared/SharedComponents';

export interface SentimentAnalysisProps {
  commentAnalytics: {
    positiveRatio: number | null;
    negativeRatio: number | null;
    neutralRatio: number | null;
  } | null;
  className?: string;
}
const colors = {
  positive: '#ff8097',
  neutral: '#ffbe82',
  negative: '#707fc0'
};

const SentimentAnalysis = (props: SentimentAnalysisProps) => {
  const { t } = useTranslation();

  const { commentAnalytics, className } = props;

  const hasSmallNumberOfComments = commentAnalytics === null;
  const { positiveRatio, neutralRatio, negativeRatio } = commentAnalytics || {};
  const dataNotAvailable = !hasSmallNumberOfComments && !positiveRatio && !neutralRatio && !negativeRatio;

  return (
    <OpacityContentWrapper
      className={className}
      header={<MainTitle help="sentimentAnalysis" title="Influencer.SentimentAnalysis" />}
      dataNotAvailable={dataNotAvailable}
    >
      <BadgeWrapper>
        <Badge bgColor={colors.positive}>
          <Img src={positiveIcon} />
          <BadgeLabel>{t('Influencer.Positive')}</BadgeLabel>
        </Badge>
        <Badge bgColor={colors.neutral}>
          <Img src={neutralIcon} />
          <BadgeLabel>{t('Influencer.Neutral')}</BadgeLabel>
        </Badge>
        <Badge bgColor={colors.negative}>
          <Img src={negativeIcon} />
          <BadgeLabel>{t('Influencer.Negative')}</BadgeLabel>
        </Badge>
      </BadgeWrapper>
      <AnalysisAreaWrapper>
        {hasSmallNumberOfComments ? (
          <p>
            {t(
              'This section cannot be displayed because the number of comments in the specified period is less than 20'
            )}
          </p>
        ) : (
          <>
            {!!positiveRatio && (
              <AnalysisArea
                value={positiveRatio}
                bgColor={colors.positive}
                title={formatPercent(positiveRatio, true, 1)}
              >
                <Value>{renderIfMoreThan10Percent({ value: positiveRatio, digits: 1 })}</Value>
              </AnalysisArea>
            )}
            {!!neutralRatio && (
              <AnalysisArea value={neutralRatio} bgColor={colors.neutral} title={formatPercent(neutralRatio, true, 1)}>
                <Value>{renderIfMoreThan10Percent({ value: neutralRatio, digits: 1 })}</Value>
              </AnalysisArea>
            )}
            {!!negativeRatio && (
              <AnalysisArea
                value={negativeRatio}
                bgColor={colors.negative}
                title={formatPercent(negativeRatio, true, 1)}
              >
                <Value>{renderIfMoreThan10Percent({ value: negativeRatio, digits: 1 })}</Value>
              </AnalysisArea>
            )}
          </>
        )}
      </AnalysisAreaWrapper>
    </OpacityContentWrapper>
  );
};

const MainTitle = styled(TitleWithHelp)`
  margin-bottom: 16px;
`;
const BadgeWrapper = styled.div`
  display: flex;
  margin-bottom: 16px;
`;
const Badge = styled.div<{ bgColor: string }>`
  height: 32px;
  border-radius: 24px;
  background-color: ${({ bgColor }) => bgColor};
  line-height: 32px;
  display: flex;
  flex: 1;
  padding-left: 4px;
  margin-right: 4px;

  &:last-child {
    margin-right: 0;
  }
`;
const BadgeLabel = styled.p`
  font-size: 13px;
  font-weight: 600;
  color: #fff;
`;
const Img = styled.img`
  width: 24px;
  margin-right: 8px;
`;
const AnalysisAreaWrapper = styled.div`
  display: flex;
  flex: 1;
`;
const AnalysisArea = styled.div<{ bgColor: string; value: number }>`
  background-color: ${({ bgColor }) => bgColor};
  width: ${({ value }) => `${formatPercent(value)}`};
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;

  &:first-of-type {
    border-top-left-radius: 5px;
    border-bottom-left-radius: 5px;
  }

  &:last-of-type {
    border-top-right-radius: 5px;
    border-bottom-right-radius: 5px;
  }

  &:hover {
    box-shadow: 0 3px 4px 0 rgba(39, 49, 59, 0.2);
    margin-bottom: 2px;
    margin-top: -2px;
  }
`;
const Value = styled.p`
  font-size: 16px;
  font-weight: 600;
  color: #fff;
`;

export default SentimentAnalysis;
